import Modeling from "bpmn-js/lib/features/modeling/Modeling";
import ElementFactory from "diagram-js/lib/core/ElementFactory";
import Create from "diagram-js/lib/features/create/Create";
import GlobalConnect from "diagram-js/lib/features/global-connect/GlobalConnect";
import HandTool from "diagram-js/lib/features/hand-tool/HandTool";
import LassoTool from "diagram-js/lib/features/lasso-tool/LassoTool";
import Palette from "diagram-js/lib/features/palette/Palette";
import SpaceTool from "diagram-js/lib/features/space-tool/SpaceTool";
import _ from "lodash";
import Moddle from "moddle/lib/moddle";
import { ElementType } from "../IProcessData";
import { USE_DMN_MODE, USE_STATEMENT_MODE } from "../ElementFactory";

/**
 * A palette provider for BPMN 2.0 elements.
 * Base copied from bpmn-js/lib/features/palette/PaletteProvider.js Latest changes to file was commit 249e584 version 6.30.0
 */
// tslint:disable variable-name
export default class CustomPaletteProvider {

    private create: Create;
    private elementFactory: ElementFactory;
    private spaceTool: SpaceTool;
    private lassoTool: LassoTool;
    private handTool: HandTool;
    private globalConnect: GlobalConnect;
    private moddle: Moddle;
    private modeling: Modeling;

    constructor(palette: Palette, create: Create, moddle: Moddle, elementFactory: ElementFactory, modeling: Modeling, spaceTool: SpaceTool, lassoTool: LassoTool, handTool: HandTool, globalConnect: GlobalConnect) {
        this.create = create;
        this.elementFactory = elementFactory;
        this.spaceTool = spaceTool;
        this.lassoTool = lassoTool;
        this.handTool = handTool;
        this.globalConnect = globalConnect;
        this.moddle = moddle;
        this.modeling = modeling;
        palette.registerProvider(this);
    }

    public getPaletteEntries = (element: any) => {

        const actions = {};
        const {
            create,
            elementFactory,
            moddle,
            modeling,
            handTool,
            lassoTool,
            spaceTool,
            globalConnect
        } = this;

        function startCreateScriptTask(event: any) {
            const scriptTaskShape = elementFactory.create("shape", { type: "bpmn:ScriptTask" });
            // The script task shape has custom rendering, and it is a bit smaller.
            // We need to provide the right size for the handle position to be drawn correctly during drag (ACC-7217):
            scriptTaskShape.height = 30;
            scriptTaskShape.width = 30;
            create.start(event, scriptTaskShape);
        }

        function startCreateStatementScriptTask(event: any) {
            const scriptTaskShape = elementFactory.create("shape", { type: "bpmn:ScriptTask", [USE_STATEMENT_MODE]: true });
            create.start(event, scriptTaskShape);
        }

        function startCreateDmnTask(event: any) {
            const dmnTaskShape = elementFactory.create("shape", { type: "bpmn:BusinessRuleTask", [USE_DMN_MODE]: true });
            create.start(event, dmnTaskShape);
        }

        const createLoopCharacteristics = (elementType: ElementType) => {
            switch (elementType) {
                case ElementType.SequentialForeach: {
                    const loopCharacteristics = moddle.create("bpmn:MultiInstanceLoopCharacteristics");
                    loopCharacteristics.isSequential = true;
                    return loopCharacteristics;
                } case ElementType.While: {
                    const loopCharacteristics = moddle.create("bpmn:StandardLoopCharacteristics");
                    loopCharacteristics.isLoop = true;
                    return loopCharacteristics;
                }
                default:
                    throw Error(`Trying to create unsupported loop type '${elementType}'.`);
            }
        };

        const createAction = (type: string, group: string, className: string, title: string, eventDefinitionType?: string) => {

            const createListener = (event: any) => {
                const shape = elementFactory.createShape(_.assign({ type: type }));
                if (eventDefinitionType) {
                    const evetDefinition = moddle.create(eventDefinitionType);
                    modeling.updateProperties(shape, { eventDefinitions: [evetDefinition] });
                }
                create.start(event, shape);
            };

            const shortType = type.replace(/^bpmn:/, "");

            return {
                group: group,
                className: className,
                title: title || `Create ${shortType}`,
                action: {
                    dragstart: createListener,
                    click: createListener
                }
            };
        };
        const createSubprocess = (className: string, title: string, loopCharacteristics?: any) => {
            const createListener = (event: any) => {
                const subProcess = elementFactory.createShape({
                    type: "bpmn:SubProcess",
                    x: 0,
                    y: 0,
                    isExpanded: true
                });
                if (loopCharacteristics) {
                    modeling.updateProperties(subProcess, { loopCharacteristics: loopCharacteristics });
                }
                const startEvent = elementFactory.createShape({
                    type: "bpmn:StartEvent",
                    x: 40,
                    y: 82,
                    parent: subProcess
                });

                create.start(event, [subProcess, startEvent], {
                    hints: {
                        autoSelect: [subProcess]
                    }
                });
            };

            return {
                group: "scope",
                className: className,
                title: title,
                action: {
                    dragstart: createListener,
                    click: createListener
                }
            };
        };

        _.assign(actions, {
            "hand-tool": {
                group: "tools",
                className: "bpmn-icon-hand-tool",
                title: "Activate the hand tool",
                action: {
                    click: (event: any) => {
                        handTool.activateHand(event);
                    }
                }
            },
            "lasso-tool": {
                group: "tools",
                className: "bpmn-icon-lasso-tool",
                title: "Activate the lasso tool",
                action: {
                    click: (event: any) => {
                        lassoTool.activateSelection(event);
                    }
                }
            },
            "space-tool": {
                group: "tools",
                className: "bpmn-icon-space-tool",
                title: "Activate the create/remove space tool",
                action: {
                    click: (event: any) => {
                        spaceTool.activateSelection(event);
                    }
                }
            },
            "global-connect-tool": {
                group: "tools",
                className: "bpmn-icon-connection-multi",
                title: "Activate the global connect tool",
                action: {
                    click: (event: any) => {
                        globalConnect.toggle(event);
                    }
                }
            },
            "tool-separator": {
                group: "tools",
                separator: true
            },
            "create.start-event": createAction(
                "bpmn:StartEvent", "event", "bpmn-icon-start-event-none", "Create Start"
            ),
            "create.end-event": createAction(
                "bpmn:EndEvent", "event", "bpmn-icon-end-event-none", "Create Return"
            ),
            "create.exclusive-gateway": createAction(
                "bpmn:ExclusiveGateway", "conditional", "bpmn-icon-gateway-xor", "Create Exclusive Decision"
            ),
            "create.inclusive-gateway": createAction(
                "bpmn:InclusiveGateway", "conditional", "bpmn-icon-gateway-or", "Create Inclusive Decision"
            ),
            "create.throw-event": createAction(
                "bpmn:IntermediateThrowEvent", "conditional", "bpmn-icon-intermediate-event-throw-signal", "Create Throw", "bpmn:SignalEventDefinition"
            ),
            "create.signal-end-event": createAction(
                "bpmn:EndEvent", "conditional", "bpmn-icon-end-event-signal", "Create Intermediate Return", "bpmn:SignalEventDefinition"
            ),
            "conditional-separator": {
                group: "conditional",
                separator: true
            },
            "create.task": createAction(
                "bpmn:Task", "activity", "bpmn-icon-task", "Create Task"
            ),
            "create.call-activity": createAction(
                "bpmn:CallActivity", "activity", "bpmn-icon-call-activity", "Create Call Subprocess"
            ),
            "create.expression-script-task": {
                group: "activity",
                title: "Create Expression",
                className: "bpmn-icon-script",
                action: {
                    dragstart: startCreateScriptTask,
                    click: startCreateScriptTask
                }
            },
            "create.statement-script-task": {
                group: "activity",
                title: "Create C# statement",
                className: "bpmn-icon-script-task",
                action: {
                    dragstart: startCreateStatementScriptTask,
                    click: startCreateStatementScriptTask
                }
            },
            "create.shared-state-task": createAction(
                "bpmn:BusinessRuleTask", "activity", "bpmn-icon-business-rule-task", "Create shared state task"
            ),
            "create.dmn-edit-task": {
                group: "activity",
                className: "bpmn-icon-business-rule-task-dmn",
                title: "Create DMN task",
                action: {
                    dragstart: startCreateDmnTask,
                    click: startCreateDmnTask
                }
            },
            "create.catch-event": createAction(
                "bpmn:IntermediateCatchEvent", "activity", "bpmn-icon-intermediate-event-catch-signal", "Create Catch", "bpmn:SignalEventDefinition"
            ),
            "activity-separator": {
                group: "activity",
                separator: true
            },
            "create.subprocess-expanded": createSubprocess(
                "bpmn-icon-subprocess-expanded", "Create Scope"
            ),
            "create.loop-subprocess": createSubprocess(
                "bpmn-icon-loop-marker", "Create While", createLoopCharacteristics(ElementType.While)
            ),
            "create.foreach-subprocess": createSubprocess(
                "bpmn-icon-sequential-mi-marker", "Create Foreach", createLoopCharacteristics(ElementType.SequentialForeach)
            ),
            "scope-separator": {
                group: "scope",
                separator: true
            },
            "create.data-object": createAction(
                "bpmn:DataObjectReference", "data-object", "bpmn-icon-data-object", "Create Data Object reference"
            ),
            "create.data-store": createAction(
                "bpmn:DataStoreReference", "data-store", "bpmn-icon-data-store", "Create Data Store reference"
            ),
            "create.group": createAction(
                "bpmn:Group", "artifact", "bpmn-icon-group", "Create Group"
            )
        });

        return actions;
    }
}
CustomPaletteProvider.$inject = [
    "palette",
    "create",
    "moddle",
    "elementFactory",
    "modeling",
    "spaceTool",
    "lassoTool",
    "handTool",
    "globalConnect"
];
